import {
  PERMISSION_LEVELS,
  PermissionLevelName,
  PermissionModule,
  UserPermissions,
  JWTClaims,
  PermissionCheckResult,
  PermissionAction,
  ACTION_PERMISSION_MAP,
  ModulePermission,
} from './types';

/**
 * Get the weight for a permission level
 */
export function getPermissionWeight(level: PermissionLevelName): number {
  const permLevel = Object.values(PERMISSION_LEVELS).find(p => p.level === level);
  return permLevel?.weight ?? 0;
}

/**
 * Compare two permission levels
 * Returns: positive if level1 > level2, negative if level1 < level2, 0 if equal
 */
export function comparePermissionLevels(
  level1: PermissionLevelName,
  level2: PermissionLevelName
): number {
  return getPermissionWeight(level1) - getPermissionWeight(level2);
}

/**
 * Check if user level meets or exceeds required level
 */
export function meetsPermissionLevel(
  userLevel: PermissionLevelName,
  requiredLevel: PermissionLevelName
): boolean {
  return getPermissionWeight(userLevel) >= getPermissionWeight(requiredLevel);
}

/**
 * Get user's permission level for a specific module
 */
export function getModulePermission(
  permissions: UserPermissions | undefined,
  module: PermissionModule
): ModulePermission {
  return permissions?.[module] ?? { level: 'None', weight: 0 };
}

/**
 * Check if user has permission for a specific action on a module
 */
export function hasPermission(
  permissions: UserPermissions | undefined,
  module: PermissionModule,
  action: PermissionAction,
  isSuperAdmin: boolean = false
): PermissionCheckResult {
  // Superadmins have all permissions
  if (isSuperAdmin) {
    return {
      hasPermission: true,
      userLevel: 'Manager',
      requiredLevel: ACTION_PERMISSION_MAP[action],
      isSuperAdmin: true,
    };
  }

  const modulePermission = getModulePermission(permissions, module);
  const requiredLevel = ACTION_PERMISSION_MAP[action];
  const hasPermissionResult = meetsPermissionLevel(modulePermission.level, requiredLevel);

  return {
    hasPermission: hasPermissionResult,
    userLevel: modulePermission.level,
    requiredLevel,
    isSuperAdmin: false,
  };
}

/**
 * Check if user can view (Viewer level or higher)
 */
export function canView(
  permissions: UserPermissions | undefined,
  module: PermissionModule,
  isSuperAdmin: boolean = false
): boolean {
  return hasPermission(permissions, module, 'view', isSuperAdmin).hasPermission;
}

/**
 * Check if user can create/edit (Editor level or higher)
 */
export function canEdit(
  permissions: UserPermissions | undefined,
  module: PermissionModule,
  isSuperAdmin: boolean = false
): boolean {
  return hasPermission(permissions, module, 'edit', isSuperAdmin).hasPermission;
}

/**
 * Check if user can delete/manage (Manager level)
 */
export function canManage(
  permissions: UserPermissions | undefined,
  module: PermissionModule,
  isSuperAdmin: boolean = false
): boolean {
  return hasPermission(permissions, module, 'manage', isSuperAdmin).hasPermission;
}

/**
 * Check if user is at least a viewer for a module
 */
export function isViewer(
  permissions: UserPermissions | undefined,
  module: PermissionModule,
  isSuperAdmin: boolean = false
): boolean {
  if (isSuperAdmin) return true;
  const modulePermission = getModulePermission(permissions, module);
  return meetsPermissionLevel(modulePermission.level, 'Viewer');
}

/**
 * Check if user is an editor for a module
 */
export function isEditor(
  permissions: UserPermissions | undefined,
  module: PermissionModule,
  isSuperAdmin: boolean = false
): boolean {
  if (isSuperAdmin) return true;
  const modulePermission = getModulePermission(permissions, module);
  return meetsPermissionLevel(modulePermission.level, 'Editor');
}

/**
 * Check if user is a manager for a module
 */
export function isManager(
  permissions: UserPermissions | undefined,
  module: PermissionModule,
  isSuperAdmin: boolean = false
): boolean {
  if (isSuperAdmin) return true;
  const modulePermission = getModulePermission(permissions, module);
  return meetsPermissionLevel(modulePermission.level, 'Manager');
}

/**
 * @deprecated Use isManager instead. Kept for backward compatibility.
 */
export function isAdmin(
  permissions: UserPermissions | undefined,
  module: PermissionModule,
  isSuperAdmin: boolean = false
): boolean {
  return isManager(permissions, module, isSuperAdmin);
}

/**
 * Fallback permissions when JWT has no custom claims (e.g. standard Firebase token).
 * Default is Viewer-only so we never grant Edit/Manage until the backend sets permissions in the token.
 */
export const FALLBACK_PERMISSIONS: UserPermissions = {
  notification_mgmt: { level: 'Viewer', weight: 10 },
  affiliate_mgmt: { level: 'Viewer', weight: 10 },
  creative_mr_oven: { level: 'Viewer', weight: 10 },
  adverity_import: { level: 'Viewer', weight: 10 },
  media_acct_mgmt: { level: 'Viewer', weight: 10 },
  brand_mgmt: { level: 'Viewer', weight: 10 },
  user_mgmt: { level: 'Viewer', weight: 10 },
  drawer_mgmt: { level: 'Viewer', weight: 10 },
  lift_ai: { level: 'Viewer', weight: 10 },
  taxonomy: { level: 'Viewer', weight: 10 },
  // grapevine is deliberately absent. It operates production background
  // jobs, so access requires an explicit IAM grant rather than the blanket
  // Viewer fallback given to a token with no custom claims.
};

export const FALLBACK_IS_SUPERADMIN = false;

/**
 * Decode a JWT token and extract claims
 * Note: This does NOT verify the token, only decodes it
 */
export function decodeJWT(token: string): JWTClaims | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    const payload = parts[1];
    if (!payload) return null;
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded) as JWTClaims;
  } catch (error) {
    console.error('Error decoding JWT:', error);
    return null;
  }
}

/**
 * Extract permissions from JWT claims.
 * If the JWT has no permissions (e.g. standard Firebase token), returns fallback permissions.
 */
export function extractPermissionsFromJWT(token: string): {
  permissions: UserPermissions;
  isSuperAdmin: boolean;
  userInfo: { name: string; email: string; picture: string };
} | null {
  const claims = decodeJWT(token);
  if (!claims) return null;

  const hasCustomPermissions =
    claims.permissions && typeof claims.permissions === 'object' && Object.keys(claims.permissions).length > 0;

  const permissions = hasCustomPermissions ? (claims.permissions as UserPermissions) : FALLBACK_PERMISSIONS;
  const isSuperAdmin = typeof claims.is_superadmin === 'boolean' ? claims.is_superadmin : FALLBACK_IS_SUPERADMIN;

  console.log('[Permissions] Source:', hasCustomPermissions ? 'JWT (custom claims)' : 'Fallback (no permissions in JWT)');
  console.log('[Permissions] is_superadmin:', isSuperAdmin);
  console.log('[Permissions] permissions:', permissions);

  return {
    permissions,
    isSuperAdmin,
    userInfo: {
      name: claims.name ?? '',
      email: claims.email ?? '',
      picture: claims.picture ?? '',
    },
  };
}

/**
 * Get all modules where user has at least viewer access
 */
export function getAccessibleModules(
  permissions: UserPermissions | undefined,
  isSuperAdmin: boolean = false
): PermissionModule[] {
  if (isSuperAdmin) {
    // Return all modules for superadmin
    return [
      'notification_mgmt',
      'affiliate_mgmt',
      'creative_mr_oven',
      'adverity_import',
      'media_acct_mgmt',
      'brand_mgmt',
      'user_mgmt',
      'taxonomy',
      'drawer_mgmt',
      'lift_ai',
    ];
  }

  if (!permissions) return [];

  return (Object.entries(permissions) as [PermissionModule, ModulePermission][])
    .filter(([_, perm]) => perm.weight >= PERMISSION_LEVELS.VIEWER.weight)
    .map(([module]) => module);
}

/**
 * Format permission level for display
 */
export function formatPermissionLevel(level: PermissionLevelName): string {
  return level;
}

/**
 * Get a human-readable description of the permission level
 */
export function getPermissionDescription(level: PermissionLevelName): string {
  switch (level) {
    case 'None':
      return 'No access to this module';
    case 'Viewer':
      return 'Can view data but cannot make changes';
    case 'Editor':
      return 'Can view and edit data';
    case 'Manager':
      return 'Full access including delete and manage';
    default:
      return 'Unknown permission level';
  }
}

